// WP-6.3 (M29 AD-3/6/7, S-32): the Advisor feed — allocation suggestions the WP-6.2 waterfall
// proposes after new income (AD-3's "New-income trigger", wired in TransactionForm.tsx).
// WP-6.4 (M29 AD-4, S-32) adds the AffordabilityBox above the feed — a plain four-part
// can/advisable/impact/timing answer for an on-demand amount+purpose check. WP-6.5 (M29 AD-5,
// S-32) adds a collapsed-by-default [Why?] expander to each — inputs → assumptions →
// confidence → trade-offs, template-rendered from the response already on hand (CA-5), never
// an LLM call.
import { Lightbulb } from 'lucide-react';
import type { SuggestionResponse } from '@pfos/shared';
import { AffordabilityBox } from '../components/AffordabilityBox';
import { Amount } from '../components/Amount';
import { EmptyState } from '../components/EmptyState';
import { ghostButtonInlineClass, primaryButtonInlineClass } from '../components/FormKit';
import { WhyExpander } from '../components/WhyExpander';
import { buildSuggestionWhy } from '../lib/why';
import {
  useAcceptSuggestion,
  useActiveSuggestions,
  useDismissSuggestion,
  useSnoozeSuggestion,
} from '../lib/suggestions';

const SNOOZE_OPTIONS: readonly { label: string; days: number }[] = [
  { label: '1 day', days: 1 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
];

function SuggestionCard({ suggestion }: { suggestion: SuggestionResponse }): React.JSX.Element {
  const acceptMutation = useAcceptSuggestion(suggestion.id);
  const snoozeMutation = useSnoozeSuggestion(suggestion.id);
  const dismissMutation = useDismissSuggestion(suggestion.id);

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <p className="mb-3 font-medium text-ink">
        New income: <Amount currency={suggestion.currency} minorUnits={suggestion.incomeMinor} />
      </p>
      <ul className="mb-3 text-sm">
        {suggestion.lines.map((line, i) => (
          <li
            key={`${line.tier}-${String(i)}`}
            className="flex items-center justify-between gap-2 border-b border-border py-1.5 last:border-0"
          >
            <span className="text-ink">
              <span className="mr-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                {line.tier}
              </span>
              {line.label}
            </span>
            <Amount
              currency={suggestion.currency}
              minorUnits={line.allocatedMinor}
              className="shrink-0 font-medium"
            />
          </li>
        ))}
      </ul>
      <WhyExpander sections={buildSuggestionWhy(suggestion)} />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={primaryButtonInlineClass}
          disabled={acceptMutation.isPending}
          onClick={() => {
            acceptMutation.mutate();
          }}
        >
          Accept
        </button>
        {SNOOZE_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            type="button"
            className={ghostButtonInlineClass}
            disabled={snoozeMutation.isPending}
            onClick={() => {
              snoozeMutation.mutate(new Date(Date.now() + opt.days * 86_400_000).toISOString());
            }}
          >
            Snooze {opt.label}
          </button>
        ))}
        <button
          type="button"
          className="text-sm text-danger hover:underline"
          disabled={dismissMutation.isPending}
          onClick={() => {
            dismissMutation.mutate();
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export function AdvisorPage(): React.JSX.Element {
  const { data: suggestions, isLoading } = useActiveSuggestions();
  // `kind` is a `z.literal('allocation')` today (the only kind that exists yet), so this reads
  // as always-true to the type checker — a real forward-compatible runtime filter for when a
  // second suggestion kind is added, not a redundant one.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const allocationSuggestions = (suggestions ?? []).filter((s) => s.kind === 'allocation');

  return (
    <div>
      <h1 className="mb-4 text-xl font-semibold text-ink">Advisor</h1>
      <AffordabilityBox />
      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {!isLoading && allocationSuggestions.length === 0 && (
        <EmptyState icon={Lightbulb} message="No suggestions right now." />
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {allocationSuggestions.map((s) => (
          <SuggestionCard key={s.id} suggestion={s} />
        ))}
      </div>
    </div>
  );
}
